import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
    getOverallCaregiverResponse,
    sortAttemptsNewestFirst,
} from '@apps/web/components/outreaches/utils';
import { PhoebeChatOrganizationScopeContext } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { getDynamicWidgetMetricTags } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type DynamicWidgetIssue,
    getDynamicWidgetSourceId,
    type OutreachProgressWidgetModel,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import {
    formatDateAndTimeWithTz,
    formatTime,
    getTimezoneAbbreviation,
} from '@apps/web/lib/date_utils';
import {
    useContactAttempts,
    useJoinedOutreach,
    useOrganization,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type CaregiverResponseType,
    type ContactAttempt,
    type ContactAttemptOutreachStatus,
    type OutreachStatus,
} from '@libraries/typescript/api_client';

const LIVE_READ_REASON = 'dynamic-agent-ui-outreach-progress';
const OUTREACH_POLL_INTERVAL_MS = 10_000;
const CONTACT_ATTEMPTS_POLL_INTERVAL_MS = 20_000;
const COLLAPSED_ROW_LIMIT = 12;

const TERMINAL_OUTREACH_STATUSES: ReadonlySet<OutreachStatus> = new Set([
    'filled',
    'unfilled',
    'cancelled',
]);

const DISPATCHED_ATTEMPT_STATUSES: ReadonlySet<ContactAttemptOutreachStatus> =
    new Set([
        'sent',
        'delivered',
        'completed',
        'live_answer',
        'voicemail_left',
        'no_answer',
        'unreachable',
        'failed',
        'transferred',
        'manually_notified',
        'call_disconnected',
    ]);

export type CaregiverAttemptRow = {
    readonly caregiverId: string;
    readonly displayName: string;
    readonly attemptsOldestFirst: ReadonlyArray<ContactAttempt>;
    readonly latestAttempt: ContactAttempt;
    readonly response: CaregiverResponseType | null;
    readonly firstContactedAt: number;
};

function parseTime(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
}

function getReadErrorStatus(error: unknown): number | null {
    if (typeof error !== 'object' || error === null || Array.isArray(error)) {
        return null;
    }
    const status = (error as Record<string, unknown>)['status'];
    return typeof status === 'number' ? status : null;
}

function groupAttemptsByCaregiver(
    attempts: ReadonlyArray<ContactAttempt>,
    displayLastFirst: boolean,
): Array<CaregiverAttemptRow> {
    const attemptsByCaregiver = new Map<string, Array<ContactAttempt>>();
    for (const attempt of attempts) {
        const caregiverAttempts =
            attemptsByCaregiver.get(attempt.caregiver_id) ?? [];
        caregiverAttempts.push(attempt);
        attemptsByCaregiver.set(attempt.caregiver_id, caregiverAttempts);
    }

    const rows: Array<CaregiverAttemptRow> = [];
    for (const [caregiverId, caregiverAttempts] of attemptsByCaregiver) {
        const newestFirst = sortAttemptsNewestFirst(caregiverAttempts);
        const latestAttempt = newestFirst[0];
        if (latestAttempt === undefined) {
            continue;
        }
        const createdTimes = caregiverAttempts
            .map((attempt) => parseTime(attempt.created_at))
            .filter((time): time is number => time !== null);
        rows.push({
            caregiverId,
            displayName: formatCaregiverName(
                latestAttempt.org_caregiver_record.name,
                displayLastFirst,
            ),
            attemptsOldestFirst: [...newestFirst].reverse(),
            latestAttempt,
            response: getOverallCaregiverResponse(caregiverAttempts),
            firstContactedAt:
                createdTimes.length > 0
                    ? Math.min(...createdTimes)
                    : Number.MAX_SAFE_INTEGER,
        });
    }
    return rows.sort(
        (left, right) => left.firstContactedAt - right.firstContactedAt,
    );
}

function buildReadIssue({
    model,
    outreachError,
    contactAttemptsError,
    hasLastValidSnapshot,
    lastValidTimeLabel,
}: {
    readonly model: OutreachProgressWidgetModel;
    readonly outreachError: unknown;
    readonly contactAttemptsError: unknown;
    readonly hasLastValidSnapshot: boolean;
    readonly lastValidTimeLabel: string | null;
}): DynamicWidgetIssue | null {
    const readError = outreachError ?? contactAttemptsError;
    if (readError === undefined) {
        return null;
    }
    const readErrorStatus = getReadErrorStatus(readError);
    let code: DynamicWidgetIssue['code'];
    if (readErrorStatus === 401 || readErrorStatus === 403) {
        code = 'permission_denied';
    } else if (hasLastValidSnapshot) {
        code = 'live_refresh_failed';
    } else {
        code = 'source_missing';
    }
    return {
        id: `${model.widgetId}.live-read.${code}`,
        code,
        level: code === 'permission_denied' ? 'error' : 'warning',
        scope: 'section',
        bindingIds: model.bindings
            .filter((binding) => binding.kind === 'live')
            .map((binding) => binding.id),
        sourceIds: model.sources
            .filter((source) => source.kind === 'resource_source')
            .map(getDynamicWidgetSourceId),
        retryable: code !== 'permission_denied',
        safeDetails: { lastValidAt: lastValidTimeLabel },
    };
}

export function useOutreachProgress(model: OutreachProgressWidgetModel) {
    const organizationScope = useContext(PhoebeChatOrganizationScopeContext);
    const organizationId = organizationScope?.organizationId ?? null;
    const timezone = organizationScope?.timezone ?? 'UTC';
    const [hasReachedTerminalStatus, setHasReachedTerminalStatus] =
        useState(false);
    const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
    const metricTags = useMemo(
        () => getDynamicWidgetMetricTags(model),
        [model],
    );
    const { data: organization } = useOrganization(organizationId, {
        reason: LIVE_READ_REASON,
    });
    const {
        data: outreach,
        error: outreachError,
        isLoading: isLoadingOutreach,
        mutate: refreshOutreach,
    } = useJoinedOutreach(
        organizationId,
        model.outreachId,
        { reason: LIVE_READ_REASON },
        undefined,
        {
            refreshIntervalMs: hasReachedTerminalStatus
                ? 0
                : OUTREACH_POLL_INTERVAL_MS,
        },
    );
    const {
        data: contactAttempts,
        error: contactAttemptsError,
        mutate: refreshContactAttempts,
    } = useContactAttempts(
        organizationId,
        model.outreachId,
        { reason: LIVE_READ_REASON },
        undefined,
        {
            refreshIntervalMs: hasReachedTerminalStatus
                ? 0
                : CONTACT_ATTEMPTS_POLL_INTERVAL_MS,
        },
    );

    const outreachStatus = outreach?.status;
    useEffect(() => {
        if (
            outreachStatus !== undefined &&
            TERMINAL_OUTREACH_STATUSES.has(outreachStatus)
        ) {
            setHasReachedTerminalStatus(true);
        }
    }, [outreachStatus]);

    useEffect(() => {
        if (outreach === undefined || outreachError !== undefined) {
            return;
        }
        setLastLoadedAt(new Date().toISOString());
    }, [contactAttempts, outreach, outreachError]);

    const displayLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const attempts = useMemo(() => contactAttempts ?? [], [contactAttempts]);
    const rows = useMemo(
        () => groupAttemptsByCaregiver(attempts, displayLastFirst),
        [attempts, displayLastFirst],
    );
    const responseCounts = outreach?.response_counts;
    const acceptedCount = responseCounts?.accepted_count ?? 0;
    const queuedCaregiverCount = outreach?.suggested_caregivers?.length ?? 0;
    const railTotal =
        queuedCaregiverCount > 0 ? queuedCaregiverCount : rows.length;
    const railDone = rows.filter((row) =>
        DISPATCHED_ATTEMPT_STATUSES.has(row.latestAttempt.outreach_status),
    ).length;
    const visibleRows = rows.slice(0, COLLAPSED_ROW_LIMIT);
    const queuedRemaining = Math.max(railTotal - visibleRows.length, 0);
    const status = outreach?.display_status ?? outreach?.status ?? null;
    const isAgentAnalyzing = outreach?.agent_status === 'analyzing';
    const clientNames = Array.from(
        new Set(
            (outreach?.shifts ?? [])
                .map((shift) => shift.patient_name)
                .filter((name) => name.length > 0),
        ),
    );
    const assignedCaregiverNames = Array.from(
        new Set(
            (outreach?.shifts ?? [])
                .map((shift) => shift.caregiver?.name)
                .filter((name): name is string => typeof name === 'string'),
        ),
    );
    const lastValidTimeLabel =
        lastLoadedAt === null
            ? null
            : `${formatTime(lastLoadedAt, timezone)} ` +
              `${getTimezoneAbbreviation(lastLoadedAt, timezone)}`;
    const asOfLabel =
        lastValidTimeLabel === null ? null : `As of ${lastValidTimeLabel}`;
    const creationFacts = [
        model.shiftIds.length > 0
            ? `${model.shiftIds.length} shift${
                  model.shiftIds.length === 1 ? '' : 's'
              }`
            : null,
        model.recurrenceDescription,
        model.scheduledStartAt === null
            ? null
            : `scheduled for ${formatDateAndTimeWithTz(
                  model.scheduledStartAt,
                  timezone,
              )}`,
    ].filter((part): part is string => part !== null);
    const readIssue = buildReadIssue({
        model,
        outreachError,
        contactAttemptsError,
        hasLastValidSnapshot:
            outreach !== undefined || contactAttempts !== undefined,
        lastValidTimeLabel,
    });
    const footerNote = [
        rows.length > 0 && queuedRemaining > 0
            ? `+${queuedRemaining} more queued`
            : null,
        asOfLabel,
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');
    const retryRead = useCallback(() => {
        void refreshOutreach();
        void refreshContactAttempts();
    }, [refreshContactAttempts, refreshOutreach]);

    return {
        timezone,
        metricTags,
        outreach,
        responseCounts,
        acceptedCount,
        queuedCaregiverCount,
        railTotal,
        railDone,
        rowCount: rows.length,
        visibleRows,
        status,
        isAgentAnalyzing,
        isLoadingOutreach,
        clientNames,
        assignedCaregiverNames,
        creationFacts,
        readIssue,
        footerNote,
        retryRead,
        hasLiveOutreach: outreach !== undefined,
        now: new Date(),
    };
}
